import React, { useState, useEffect } from 'react';
import { testimonialService } from '../../services/adminService';
import { useToast } from '../../components/Toast';
import { getImageUrl } from '../../utils/mediaUtils';
import { 
  Quote, 
  Search, 
  Trash2, 
  CheckCircle, 
  Clock, 
  RefreshCw,
  Send,
  ShieldAlert
} from 'lucide-react';
import './TestimonialApproval.css';
import './forms/FormsUnified.css';

export default function TestimonialApproval({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all'); // all, approved, pending, rejected
  const [roleFilter, setRoleFilter] = useState('all'); // all, parent, student, teacher, alumni
  const [searchQuery, setSearchQuery] = useState('');
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteConfirmItem, setDeleteConfirmItem] = useState(null);
  const [sectionId, setSectionId] = useState(193); // Default contact-testimonials section
  const [pendingChangesMap, setPendingChangesMap] = useState({}); // testimonialId -> { action, changeId, fieldName }
  const [stats, setStats] = useState({
    total: 0,
    approved: 0,
    pending: 0,
    rejected: 0,
    awaitingPrincipal: 0
  });

  const resolvedApiBase = apiBase || process.env.REACT_APP_API_BASE || 'https://permataharapansecondary.sch.id/api';

  useEffect(() => {
    loadTestimonials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedApiBase]);

  const loadTestimonials = async () => {
    setLoading(true);
    try {
      // 1. Fetch section ID for contact-testimonials
      try {
        const sRes = await fetch(`${resolvedApiBase}/homepage/cms/sections?page=contact-us`, {
          credentials: 'include'
        });
        if (sRes.ok) {
          const sJson = await sRes.json();
          const items = Array.isArray(sJson.data) ? sJson.data : Object.values(sJson.data || {});
          const found = items.find(s => s.section_type === 'contact-testimonials' || s.section_type === 'testimonials');
          if (found && found.id) {
            setSectionId(found.id);
          }
        }
      } catch (secErr) {
        console.warn('Failed to detect section id, fallback to 193:', secErr);
      }

      // 2. Fetch pending Principal approvals
      let pendingMap = {};
      try {
        const pRes = await fetch(`${resolvedApiBase}/homepage/cms/pending-approvals?limit=100`, {
          credentials: 'include'
        });
        if (pRes.ok) {
          const pJson = await pRes.json();
          const pItems = pJson.data?.items || [];
          pItems.forEach(p => {
            if (p.field_id && p.field_id.startsWith('testimonials.')) {
              const parts = p.field_id.split('.'); // testimonials.{id}.{action}
              if (parts.length >= 3) {
                const tId = parseInt(parts[1], 10);
                const act = parts[2];
                pendingMap[tId] = {
                  changeId: p.id,
                  action: act,
                  fieldName: p.field_name,
                  createdAt: p.created_at,
                  submittedBy: p.full_name || 'Admin'
                };
              }
            }
          });
        }
      } catch (pErr) {
        console.warn('Failed to load pending approvals:', pErr);
      }
      setPendingChangesMap(pendingMap);

      // 3. Fetch all non-deleted testimonials
      const res = await testimonialService.listTestimonials({ limit: 100 });
      if (res && res.ok && res.data) {
        const rawList = res.data.testimonials || [];
        const list = rawList.map(t => {
          let text = t.content || '';
          let img = t.image_url || t.image || null;

          if (text.includes('[IMAGE:')) {
            const match = text.match(/\[IMAGE:(https?:\/\/[^\]]+)\]/);
            if (match) {
              if (!img) img = match[1];
              text = text.replace(/\[IMAGE:https?:\/\/[^\]]+\]\s*/g, '').trim();
            }
          }

          if (!img) {
            if (t.author_name === 'Aletheia') {
              img = 'https://permataharapansecondary.sch.id/api/uploads/images/highlight_1792489152_6ad736c09a527.jpg';
            } else if (t.author_name === 'Kezia Chika') {
              img = 'https://permataharapansecondary.sch.id/api/uploads/images/highlight_1792486575_6ad72cafd5dc2.jpg';
            } else if (t.author_name === 'Riana') {
              img = 'https://permataharapansecondary.sch.id/api/uploads/images/highlight_1792484471_6ad724772007c.jpg';
            }
          }

          return {
            ...t,
            content: text,
            image_url: img
          };
        });
        setTestimonials(list);

        // Compute stats
        const total = list.length;
        const approved = list.filter(t => t.status === 'approved').length;
        const pending = list.filter(t => t.status === 'pending' || !t.status).length;
        const rejected = list.filter(t => t.status === 'rejected').length;
        const awaitingPrincipal = Object.keys(pendingMap).length;
        setStats({ total, approved, pending, rejected, awaitingPrincipal });
      }
    } catch (err) {
      console.error('Failed to load testimonials:', err);
      toast.error('Gagal memuat daftar testimoni');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (item) => {
    // Check if already awaiting principal approval
    if (pendingChangesMap[item.id]) {
      toast.warning(`Testimoni dari "${item.author_name}" sedang dalam antrean persetujuan Principal. Harap menunggu hingga Principal menyetujui atau menolak permohonan tersebut.`);
      return;
    }

    const isCurrentlyApproved = item.status === 'approved';
    const action = isCurrentlyApproved ? 'unpublish' : 'publish';
    const actionLabel = isCurrentlyApproved ? 'Unpublish (OFF)' : 'Publish (ON)';
    setTogglingId(item.id);

    try {
      const payload = {
        section_id: sectionId,
        page: 'contact-us',
        section_type: 'contact-testimonials',
        field_id: `testimonials.${item.id}.${action}`,
        field_name: `${actionLabel}: ${item.author_name}`,
        new_value: {
          id: item.id,
          author_name: item.author_name,
          action: action,
          author_type: item.author_type,
          content: item.content,
          image_url: item.image_url || null
        },
        old_value: item.status || 'pending',
        locale: 'en'
      };

      const res = await fetch(`${resolvedApiBase}/cms/field-change/submit`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const resData = await res.json();
      if (res.ok && (resData.ok || resData.success)) {
        toast.success(
          action === 'publish'
            ? `Permintaan aktivasi (ON) ulasan "${item.author_name}" telah dikirim ke Principal untuk persetujuan.`
            : `Permintaan penonaktifan (OFF) ulasan "${item.author_name}" telah dikirim ke Principal untuk persetujuan.`
        );

        setPendingChangesMap(prev => {
          const updated = {
            ...prev,
            [item.id]: {
              action,
              fieldName: payload.field_name,
              createdAt: new Date().toISOString()
            }
          };
          setStats(s => ({ ...s, awaitingPrincipal: Object.keys(updated).length }));
          return updated;
        });
      } else {
        throw new Error(resData?.message || 'Gagal mengajukan perubahan ke Principal');
      }
    } catch (err) {
      console.error('Toggle submit error:', err);
      toast.error(err.message || 'Gagal mengajukan status testimoni ke Principal');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDeleteClick = (item) => {
    if (pendingChangesMap[item.id]) {
      toast.warning(`Testimoni dari "${item.author_name}" sedang dalam antrean persetujuan Principal.`);
      return;
    }
    setDeleteConfirmItem(item);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmItem) return;
    const id = deleteConfirmItem.id;
    setDeletingId(id);

    try {
      const payload = {
        section_id: sectionId,
        page: 'contact-us',
        section_type: 'contact-testimonials',
        field_id: `testimonials.${id}.delete`,
        field_name: `Hapus Testimonial: ${deleteConfirmItem.author_name}`,
        new_value: {
          id: id,
          author_name: deleteConfirmItem.author_name,
          action: 'delete',
          author_type: deleteConfirmItem.author_type,
          content: deleteConfirmItem.content
        },
        old_value: deleteConfirmItem.status || 'pending',
        locale: 'en'
      };

      const res = await fetch(`${resolvedApiBase}/cms/field-change/submit`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const resData = await res.json();
      if (res.ok && (resData.ok || resData.success)) {
        toast.success(`Permintaan hapus ulasan "${deleteConfirmItem.author_name}" telah dikirim ke Principal untuk persetujuan.`);
        setPendingChangesMap(prev => {
          const updated = {
            ...prev,
            [id]: {
              action: 'delete',
              fieldName: payload.field_name,
              createdAt: new Date().toISOString()
            }
          };
          setStats(s => ({ ...s, awaitingPrincipal: Object.keys(updated).length }));
          return updated;
        });
        setDeleteConfirmItem(null);
      } else {
        throw new Error(resData?.message || 'Gagal mengajukan penghapusan ke Principal');
      }
    } catch (err) {
      console.error('Delete submit error:', err);
      toast.error(err.message || 'Gagal mengajukan penghapusan testimoni');
    } finally {
      setDeletingId(null);
    }
  };

  // Filter and search logic
  const filteredTestimonials = testimonials.filter(item => {
    // Status filter
    if (activeFilter === 'approved' && item.status !== 'approved') return false;
    if (activeFilter === 'pending' && item.status !== 'pending' && item.status !== null && item.status !== '') return false;
    if (activeFilter === 'rejected' && item.status !== 'rejected') return false;
    if (activeFilter === 'awaiting' && !pendingChangesMap[item.id]) return false;

    // Role filter
    if (roleFilter !== 'all') {
      const normRole = (item.author_type || '').toLowerCase();
      if (normRole !== roleFilter.toLowerCase()) return false;
    }

    // Search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const name = (item.author_name || '').toLowerCase();
      const content = (item.content || '').toLowerCase();
      const email = (item.author_email || '').toLowerCase();
      if (!name.includes(q) && !content.includes(q) && !email.includes(q)) return false;
    }

    return true;
  });

  const getRoleBadgeStyle = (role) => {
    const r = String(role || '').toLowerCase();
    switch (r) {
      case 'parent':
        return { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe', label: 'Parent / Orang Tua' };
      case 'student':
        return { bg: '#f5f3ff', color: '#7c3aed', border: '#ddd6fe', label: 'Student / Siswa' };
      case 'teacher':
        return { bg: '#fffbeb', color: '#b45309', border: '#fde68a', label: 'Teacher / Guru' };
      case 'alumni':
        return { bg: '#ecfdf5', color: '#047857', border: '#a7f3d0', label: 'Alumni' };
      default:
        return { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0', label: role || 'Pengunjung' };
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map(p => p[0].toUpperCase())
      .join('');
  };

  return (
    <div className="unified-master-container" style={{ padding: '0 24px 40px' }}>
      <ToastContainer />

      {/* TOP HEADER */}
      <div className="unified-header-card" style={{ marginBottom: '24px' }}>
        <div className="unified-header-icon-box" style={{ background: '#7c3aed', color: '#ffffff' }}>
          <Quote size={24} />
        </div>
        <div className="unified-header-text">
          <h2>Testimonials Review & Moderation</h2>
          <p>
            Tinjau ulasan pengunjung dari form publik. Setiap perubahan status (ON / OFF) atau penghapusan akan diajukan ke Kepala Sekolah (Principal Approval) sebelum diterapkan ke website publik.
          </p>
        </div>
        <button
          type="button"
          className="unified-secondary-btn"
          onClick={loadTestimonials}
          disabled={loading}
          style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          <span>Refresh Data</span>
        </button>
      </div>

      {/* STATS SUMMARY BAR */}
      <div className="t-stats-grid">
        <div className="t-stat-card">
          <div className="t-stat-label">Total Testimoni</div>
          <div className="t-stat-value">{stats.total}</div>
        </div>
        <div className="t-stat-card published">
          <div className="t-stat-label">Terpublikasi (ON)</div>
          <div className="t-stat-value" style={{ color: '#059669' }}>{stats.approved}</div>
        </div>
        <div className="t-stat-card pending">
          <div className="t-stat-label">Belum Aktif (OFF)</div>
          <div className="t-stat-value" style={{ color: '#64748b' }}>{stats.pending}</div>
        </div>
        <div className="t-stat-card awaiting">
          <div className="t-stat-label">Menunggu Persetujuan Principal</div>
          <div className="t-stat-value" style={{ color: '#d97706' }}>{stats.awaitingPrincipal}</div>
        </div>
      </div>

      {/* CONTROLS: SEARCH & FILTERS */}
      <div className="t-controls-bar">
        <div className="t-tabs-group">
          <button
            type="button"
            className={`t-filter-tab ${activeFilter === 'all' ? 'active' : ''}`}
            onClick={() => setActiveFilter('all')}
          >
            Semua ({stats.total})
          </button>
          <button
            type="button"
            className={`t-filter-tab ${activeFilter === 'approved' ? 'active' : ''}`}
            onClick={() => setActiveFilter('approved')}
          >
            <CheckCircle size={13} color="#059669" />
            <span>Terpublikasi ({stats.approved})</span>
          </button>
          <button
            type="button"
            className={`t-filter-tab ${activeFilter === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveFilter('pending')}
          >
            <Clock size={13} color="#64748b" />
            <span>Belum Aktif ({stats.pending})</span>
          </button>
          {stats.awaitingPrincipal > 0 && (
            <button
              type="button"
              className={`t-filter-tab ${activeFilter === 'awaiting' ? 'active' : ''}`}
              onClick={() => setActiveFilter('awaiting')}
            >
              <Send size={13} color="#d97706" />
              <span>Menunggu Approval ({stats.awaitingPrincipal})</span>
            </button>
          )}
        </div>

        <div className="t-search-group">
          {/* Role select */}
          <select 
            className="unified-input"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            style={{ width: '160px', padding: '8px 12px', fontSize: '13px' }}
          >
            <option value="all">Semua Peran</option>
            <option value="parent">Parent (Orang Tua)</option>
            <option value="student">Student (Siswa)</option>
            <option value="teacher">Teacher (Guru)</option>
            <option value="alumni">Alumni</option>
          </select>

          {/* Search box */}
          <div style={{ position: 'relative', width: '260px' }}>
            <Search size={15} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              type="text"
              className="unified-input"
              placeholder="Cari nama atau pesan..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ paddingLeft: '34px', fontSize: '13px' }}
            />
          </div>
        </div>
      </div>

      {/* CONTENT LIST / GRID */}
      {loading ? (
        <div className="unified-loading-wrap" style={{ minHeight: '260px' }}>
          <div className="unified-spinner" />
          <p>Memuat data ulasan...</p>
        </div>
      ) : filteredTestimonials.length === 0 ? (
        <div style={{
          background: '#ffffff',
          borderRadius: '16px',
          padding: '60px 20px',
          textAlign: 'center',
          border: '1px dashed #cbd5e1',
          marginTop: '16px'
        }}>
          <Quote size={44} color="#cbd5e1" style={{ margin: '0 auto 14px' }} />
          <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#334155', margin: '0 0 6px' }}>
            Tidak ada ulasan yang sesuai filter
          </h3>
          <p style={{ fontSize: '13px', color: '#64748b', maxWidth: '400px', margin: '0 auto' }}>
            {testimonials.length === 0 
              ? 'Belum ada testimoni yang dikirimkan oleh pengunjung melalui form publik.'
              : 'Silakan ubah kata kunci pencarian atau tab filter untuk melihat ulasan lain.'}
          </p>
        </div>
      ) : (
        <div className="t-cards-grid">
          {filteredTestimonials.map(item => {
            const isApproved = item.status === 'approved';
            const isToggling = togglingId === item.id;
            const pendingChange = pendingChangesMap[item.id];
            const hasPending = Boolean(pendingChange);
            const roleStyle = getRoleBadgeStyle(item.author_type);
            const photoUrl = item.image_url ? getImageUrl(item.image_url) : (item.image ? getImageUrl(item.image) : null);

            return (
              <div 
                key={item.id} 
                className={`t-moderation-card ${isApproved ? 'is-live' : 'is-off'} ${hasPending ? 'is-pending-approval' : ''}`}
              >
                {/* CARD TOP ROW: AUTHOR INFO & ACTIONS */}
                <div className="t-card-header">
                  <div className="t-author-cluster">
                    {photoUrl ? (
                      <div className="t-avatar-box">
                        <img 
                          src={photoUrl} 
                          alt={item.author_name} 
                          onError={(e) => {
                            e.target.style.display = 'none';
                            if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div 
                          className="t-avatar-fallback" 
                          style={{ display: 'none' }}
                        >
                          {getInitials(item.author_name)}
                        </div>
                      </div>
                    ) : (
                      <div className="t-avatar-fallback">
                        {getInitials(item.author_name)}
                      </div>
                    )}
                    <div className="t-author-details">
                      <h4 className="t-author-name">{item.author_name}</h4>
                      <span 
                        className="t-role-pill" 
                        style={{ 
                          background: roleStyle.bg, 
                          color: roleStyle.color, 
                          borderColor: roleStyle.border 
                        }}
                      >
                        {roleStyle.label}
                      </span>
                    </div>
                  </div>

                  <div className="t-card-actions-cluster">
                    {/* Delete button (submits deletion request) */}
                    <button
                      type="button"
                      className="t-btn-icon-delete"
                      onClick={() => handleDeleteClick(item)}
                      title={hasPending ? 'Permintaan sedang ditinjau oleh Principal' : 'Ajukan Hapus Testimoni'}
                      disabled={deletingId === item.id || hasPending}
                      style={{ opacity: hasPending ? 0.4 : 1 }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {/* TESTIMONIAL QUOTE BODY */}
                <div className="t-quote-box">
                  <p className="t-quote-text">
                    "{item.content}"
                  </p>
                </div>

                {/* PENDING APPROVAL NOTICE IF SUBMITTED */}
                {hasPending && (
                  <div className="t-pending-approval-banner">
                    <Clock size={14} />
                    <span>
                      {pendingChange.action === 'publish'
                        ? 'Menunggu Persetujuan Principal: Permintaan ON (Publish)'
                        : pendingChange.action === 'unpublish'
                        ? 'Menunggu Persetujuan Principal: Permintaan OFF (Unpublish)'
                        : 'Menunggu Persetujuan Principal: Permintaan Hapus'}
                    </span>
                  </div>
                )}

                {/* CARD FOOTER: DATE & ON/OFF SWITCH */}
                <div className="t-card-footer">
                  <div className="t-date-info">
                    <Clock size={12} color="#94a3b8" />
                    <span>{item.created_at ? new Date(item.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Baru saja'}</span>
                  </div>

                  {/* TOGGLE SWITCH (ON / OFF) */}
                  <div className="t-switch-cluster">
                    <span className={`t-switch-label ${isApproved ? 'active' : ''}`}>
                      {isApproved ? 'PUBLISHED (ON)' : 'UNPUBLISHED (OFF)'}
                    </span>
                    <button
                      type="button"
                      className={`t-toggle-switch ${isApproved ? 'on' : 'off'} ${isToggling ? 'toggling' : ''}`}
                      onClick={() => handleToggleStatus(item)}
                      disabled={isToggling || hasPending}
                      title={
                        hasPending
                          ? 'Perubahan sedang ditinjau oleh Principal'
                          : isApproved
                          ? 'Klik untuk mengajukan penonaktifan (OFF) ke Principal'
                          : 'Klik untuk mengajukan publikasi (ON) ke Principal'
                      }
                      style={{ opacity: hasPending ? 0.5 : 1 }}
                    >
                      <span className="t-toggle-knob" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {deleteConfirmItem && (
        <div className="t-modal-backdrop">
          <div className="t-modal-box">
            <div className="t-modal-icon-wrap" style={{ background: '#fee2e2' }}>
              <ShieldAlert size={32} color="#dc2626" />
            </div>
            <h3>Ajukan Penghapusan Testimoni?</h3>
            <p>
              Permintaan penghapusan testimoni dari <strong>"{deleteConfirmItem.author_name}"</strong> akan diajukan ke antrean persetujuan <strong>Principal (Kepala Sekolah)</strong> sebelum data dihapus secara permanen.
            </p>
            <div className="t-modal-actions">
              <button
                type="button"
                className="t-btn-modal-cancel"
                onClick={() => setDeleteConfirmItem(null)}
                disabled={deletingId !== null}
              >
                Batal
              </button>
              <button
                type="button"
                className="t-btn-modal-delete"
                onClick={handleConfirmDelete}
                disabled={deletingId !== null}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
              >
                <Send size={14} />
                <span>{deletingId ? 'Mengajukan...' : 'Kirim Permintaan ke Principal'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
